import { Calendar } from '@nextforce/api'
import { Button, Divider, Icon, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { isEqual } from 'lodash'
import React, { useCallback, useState } from 'react'
import { FlatList, Modal, View } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { areDefaultFilters } from '../context'
import { Filter, FilterName, Filters, Preset } from '../types'
import { FilterRow } from './row'

interface Props {
  currentFilters: Filters
  filters: Filters
  onPressFilter(filter: Filter, booleanValue?: boolean): void
  onApplyFilters: () => void
  onSelectPreset: (selectedPreset?: Preset) => void
  savedPresets: Preset[] | undefined
  currentSelectedPresetIndex: number | undefined
  onReset: () => void
  onPressSave: () => void
  scheduleCalendar: Calendar | undefined
}

export function FilterChooserView({
  currentFilters,
  filters,
  onPressFilter,
  onApplyFilters,
  onSelectPreset,
  savedPresets,
  currentSelectedPresetIndex,
  onPressSave,
  onReset,
  scheduleCalendar,
}: Props) {
  const styles = useStyles()

  const onPressPreset = useCallback(
    (selectedPreset: Preset) => {
      if (currentSelectedPresetIndex === selectedPreset.id) {
        onSelectPreset(undefined)
      } else {
        onSelectPreset(selectedPreset)
      }
    },
    [onSelectPreset, currentSelectedPresetIndex]
  )

  return (
    <View style={styles.container}>
      <FlatList
        style={styles.list}
        data={Object.keys(currentFilters) as FilterName[]}
        keyExtractor={(item: FilterName) => String(currentFilters[item].id)}
        renderItem={({ item }) =>
          isToggledOff(item, currentFilters) ? null : (
            <FilterRow filter={currentFilters[item]} onPress={onPressFilter} />
          )
        }
        ListFooterComponent={
          <View>
            <View style={styles.filtersButtonsContainer}>
              <Button
                title={t('save')}
                onPress={onPressSave}
                containerStyle={styles.buttonContainer}
                disabled={
                  areDefaultFilters(currentFilters) ||
                  currentSelectedPresetIndex !== undefined
                }
              />
              <Button
                title={t('apply')}
                onPress={onApplyFilters}
                containerStyle={styles.buttonContainer}
                disabled={isEqual(filters, currentFilters)}
              />
              <Button
                title={t('reset')}
                onPress={onReset}
                containerStyle={styles.buttonContainer}
                buttonStyle={styles.resetButton}
                titleStyle={styles.resetButtonTitle}
                disabled={areDefaultFilters(currentFilters)}
              />
            </View>
            <Divider />
            <View style={styles.savedFiltersContainer}>
              <Text style={{ fontWeight: 'bold', fontSize: 16 }}>
                {t('savedFilters')}
              </Text>
              <View style={styles.presetButtonsContainer}>
                {savedPresets && savedPresets.length > 0
                  ? savedPresets.map((preset, index) => (
                      <Button
                        title={`${preset.id + 1}`}
                        onPress={() => onPressPreset(preset)}
                        containerStyle={[
                          styles.presetButtonContainer,
                          currentSelectedPresetIndex === index
                            ? styles.selectedPresetButton
                            : styles.presetButton,
                        ]}
                        buttonStyle={
                          currentSelectedPresetIndex === index
                            ? styles.selectedPresetButton
                            : styles.presetButton
                        }
                        titleStyle={[
                          currentSelectedPresetIndex === index
                            ? styles.selectedPresetButtonTitle
                            : styles.presetButtonTitle,
                          areDefaultFilters(preset.filters)
                            ? {}
                            : styles.savedPresetButtonTitle,
                        ]}
                        key={String(index)}
                      />
                    ))
                  : null}
              </View>
              {scheduleCalendar === 'helper' ? (
                <NextShiftFiltersExplanationView />
              ) : null}
            </View>
          </View>
        }
      />
    </View>
  )
}

function NextShiftFiltersExplanationView() {
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const styles = useStyles()

  return (
    <View style={styles.filtersExplanationContainer}>
      <View style={styles.iconContainer}>
        <PressableOpacity onPress={() => setModalVisible(true)}>
          <Icon
            name="alert-circle"
            type="ionicon"
            size={24}
            color={'#007aff'}
          />
        </PressableOpacity>
        <Text style={styles.text}>{t('filtersHelpTitle')}</Text>
      </View>
      <Modal animationType="fade" transparent={true} visible={modalVisible}>
        <PressableOpacity
          style={styles.modalBackDrop}
          onPress={() => setModalVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalView}>
              <Text style={styles.modalText}>
                {t('filtersExplanationMessage')}
              </Text>
            </View>
          </View>
        </PressableOpacity>
      </Modal>
    </View>
  )
}

function isToggledOff(togglingFilterName?: FilterName, filters?: Filters) {
  if (togglingFilterName) {
    const togglingFilter = filters && filters[togglingFilterName]
    return !togglingFilter?.visible
  } else return false
}

const useStyles = makeStyles(theme => ({
  container: {
    marginHorizontal: 0,
    paddingHorizontal: 0,
    flex: 1,
  },
  list: {
    paddingTop: 5,
    marginBottom: 5,
  },
  buttonContainer: {
    margin: 10,
  },
  filtersButtonsContainer: {
    flexDirection: 'row-reverse',
  },
  resetButton: {
    backgroundColor: 'white',
  },
  resetButtonTitle: {
    color: theme.colors?.primary,
  },
  saveFiltersHeading: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  saveFiltersText: {
    marginVertical: 10,
  },
  savedFiltersContainer: {
    marginTop: 10,
    marginHorizontal: 20,
  },
  presetButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    flex: 1,
    maxWidth: 400,
    marginTop: 10,
  },
  presetButtonContainer: {
    flex: 1,
    justifyContent: 'center',
    margin: 10,
    borderRadius: 50,
    borderColor: theme.colors?.primary,
    borderWidth: 1,
    height: 100,
    maxWidth: 100,
  },
  presetButton: {
    backgroundColor: 'white',
  },
  presetButtonTitle: {
    color: theme.colors?.primary,
    fontWeight: 'normal',
    fontSize: 42,
  },
  selectedPresetButton: {
    backgroundColor: theme.colors?.primary,
  },
  selectedPresetButtonTitle: {
    fontFamily: font.bold,
    color: 'white',
    fontWeight: 'bold',
    fontSize: 42,
  },
  savedPresetButtonTitle: {
    fontFamily: font.bold,
    fontWeight: 'bold',
  },
  filtersExplanationContainer: {
    flexDirection: 'row',
    alignContent: 'flex-start',
  },
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    fontSize: 16,
    margin: 5,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalView: {
    margin: 10,
    backgroundColor: theme.colors?.grey5,
    borderRadius: 20,
    padding: 25,
    alignItems: 'center',
    shadowColor: theme.colors?.black,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalBackDrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.50)',
  },
  modalText: {
    textAlign: 'center',
    fontSize: 16,
    color: theme.colors?.black,
  },
}))
